import React from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

export default function Development() {
  return (
    <div className="section">
      <h1 className="fw-bold mb-3">Development</h1>
      <p className="fs-5 mb-2">
        M3 Computing delivers senior-level engineering across two distinct disciplines:
        cloud-native development and SaaS product development. Both are grounded in the same
        commitment — we build things that work in production, that are fully tested and
        documented, and that your team can maintain and extend without us.
      </p>
      <p className="fs-5 mb-5">
        There are no handoffs to junior engineers. The expertise you engage is the expertise
        that does the work.
      </p>

      <hr className="m3-divider mb-5" />

      <h2 className="fw-bold mb-3">Cloud-Native Development</h2>
      <p className="mb-4">
        Platform engineering, Kubernetes, and microservices are not commodity skills. We design
        and build the infrastructure that modern applications run on — container platforms,
        service meshes, CI/CD pipelines, observability stacks, and the APIs that connect them.
        We work primarily in Java, Go, and Python, and we design for the operational realities
        of running software at scale: reliability, security, and cost efficiency.
      </p>

      <Row className="g-4 mb-5">
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">Kubernetes &amp; Platform Engineering</h3>
          <p className="mb-0">
            Cluster design, operator development, workload management, and the internal
            developer platform tooling that makes Kubernetes usable for engineering teams
            beyond the platform team itself.
          </p>
        </Col>
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">Microservices Architecture &amp; Development</h3>
          <p className="mb-0">
            Service decomposition, inter-service communication patterns, asynchronous
            messaging, and the bounded context design that makes microservices maintainable
            rather than just distributed.
          </p>
        </Col>
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">API Design &amp; Development</h3>
          <p className="mb-0">
            REST and GraphQL APIs designed for longevity — versioning strategies, contract
            testing, OpenAPI documentation, and the security patterns that protect them.
          </p>
        </Col>
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">CI/CD &amp; DevOps</h3>
          <p className="mb-0">
            Pipeline design and implementation that makes continuous deployment safe and
            repeatable. From build automation to deployment strategies to observability
            and alerting.
          </p>
        </Col>
      </Row>

      <hr className="m3-divider mb-5" />

      <h2 className="fw-bold mb-3">SaaS Development</h2>
      <p className="mb-4">
        Building a SaaS product means making foundational architectural decisions that are
        expensive to revisit. Multi-tenancy, tenant isolation, subscription management,
        self-service onboarding, usage tracking — these patterns are well understood, but
        implementing them wrong creates technical debt that compounds with every customer
        you add.
      </p>
      <p className="mb-4">
        We design and build SaaS platforms with the right patterns from the start, working
        across the full lifecycle: architecture, development, testing, deployment, and
        documentation. You own the code, and we make sure your team understands it.
      </p>

      <Row className="g-4">
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">Multi-Tenant Architecture</h3>
          <p className="mb-0">
            Tenant isolation models, shared vs. dedicated infrastructure tradeoffs, data
            partitioning strategies, and the security boundaries that keep customers' data
            separated at every layer.
          </p>
        </Col>
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">Full-Cycle SaaS Product Development</h3>
          <p className="mb-0">
            From architecture design through production deployment. We build the application,
            the admin tooling, the operational infrastructure, and the integrations — and we
            hand it over with the documentation your team needs to own it.
          </p>
        </Col>
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">SaaS Platform Modernization</h3>
          <p className="mb-0">
            For teams that have outgrown their current architecture. We assess what's limiting
            growth, design the path forward, and work incrementally to move the platform
            without disrupting customers or development velocity.
          </p>
        </Col>
        <Col md={6}>
          <h3 className="h5 fw-bold mb-2">Integration &amp; API Development</h3>
          <p className="mb-0">
            Payment processing, identity and access management, CRM, billing, and the
            third-party integrations that modern SaaS products depend on to operate at scale.
          </p>
        </Col>
      </Row>
    </div>
  );
}
